import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { formatDistanceToNow, isToday, isYesterday, subDays, subWeeks, subMonths, differenceInDays, differenceInWeeks, differenceInMonths } from 'date-fns';
import CSpinner from './CSpinner';

const HistorySidebar = ({ isOpen, historyList = [], isLoading, searchTerm = '', onSearchTermChange, onHistoryItemClick, onViewToolsClick, onRefreshHistory }) => {
  
  const [hoveredItem, setHoveredItem] = useState(null);

  const filteredHistory = historyList && Array.isArray(historyList) 
  ? historyList.filter(item => item.query.toLowerCase().includes(searchTerm.toLowerCase()))
  : [];

  // Group history by time periods: Today, Yesterday, This Week, Last Week, This Month, etc.
  const groupHistoryByTimePeriod = () => {
    const groups = {
      'Today': [],
      'Yesterday': [],
      'This Week': [],
      'Last Week': [],
      'This Month': [],
      'Last Month': [],
      'Older': []
    };
    
    const now = new Date();
    
    filteredHistory.forEach(item => {
      const date = new Date(item.timestamp);
      
      if (isToday(date)) {
        groups['Today'].push(item);
      } else if (isYesterday(date)) {
        groups['Yesterday'].push(item);
      } else if (differenceInDays(now, date) < 7) {
        groups['This Week'].push(item);
      } else if (differenceInDays(now, date) < 14) {
        groups['Last Week'].push(item);
      } else if (differenceInMonths(now, date) < 1) {
        groups['This Month'].push(item);
      } else if (differenceInMonths(now, date) < 2) {
        groups['Last Month'].push(item);
      } else {
        groups['Older'].push(item);
      }
    });
    
    // Remove empty groups
    return Object.fromEntries(
      Object.entries(groups).filter(([_, items]) => items.length > 0)
    );
  };

  const groupedHistory = groupHistoryByTimePeriod();

  // Animation variants for sidebar
  const sidebarVariants = {
    open: { 
      width: "320px", 
      transition: { duration: 0.3 } 
    },
    closed: { 
      width: "0px", 
      transition: { duration: 0.3 } 
    }
  };

  // Helper function for background color
  const getItemBackground = (index, isHovered) => {
    if (isHovered) return "bg-blue-50";
    return index % 2 === 0 ? "bg-gray-50" : "bg-white";
  };

  return (
    <motion.div 
      className="fixed top-15 left-0 h-screen bg-white shadow-md z-50 overflow-hidden flex flex-col"
      variants={sidebarVariants}
      animate={isOpen ? "open" : "closed"}
      initial={false}>
      {isOpen && (
        <div className="h-full flex flex-col">
          {/* Sidebar Header */}
          <div className="p-4 border-b border-gray-100 bg-white sticky top-0 z-10">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">Search History</h2>
              <button 
                onClick={onRefreshHistory} 
                className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center p-1 rounded-full hover:bg-blue-50 transition-colors"
                aria-label="Refresh history"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            </div>
            
            {/* Search Input */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <svg className="w-4 h-4 text-gray-500" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
                  <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"/>
                </svg>
              </div>
              <input 
                type="search" 
                className="block w-full p-2 pl-10 text-sm text-gray-900 border border-gray-200 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Filter history..." 
                value={searchTerm}
                onChange={(e) => onSearchTermChange(e.target.value)}
              />
            </div>
          </div>
          
          {/* Main History Content Area */}
          <div className="flex-1 overflow-y-auto custom-scrollbar">
            {/* History Loading State */}
            {isLoading && (
              <div className="flex justify-center items-center p-6">
                <CSpinner />
              </div>
            )}
            
            {/* Empty History State */}
            {!isLoading && historyList.length === 0 && (
              <div className="flex flex-col items-center justify-center p-6 text-center">
                <svg className="w-12 h-12 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <h3 className="text-md font-medium text-gray-900 mb-1">No search history</h3>
                <p className="text-sm text-gray-500">Start searching to see your history</p>
              </div>
            )}
            
            {/* No Results State */}
            {!isLoading && historyList.length > 0 && filteredHistory.length === 0 && (
              <div className="flex flex-col items-center justify-center p-6 text-center">
                <svg className="w-12 h-12 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <h3 className="text-md font-medium text-gray-900 mb-1">No matching results</h3>
                <p className="text-sm text-gray-500">Try a different search term</p>
              </div>
            )}
            
            {/* History List with Time-Based Grouping */}
            {!isLoading && filteredHistory.length > 0 && (
              <div>
                {Object.entries(groupedHistory).map(([timeGroup, items]) => (
                  <div key={timeGroup} className="py-3">
                    <h3 className="text-xs font-medium text-gray-500 uppercase mb-1 px-4 sticky top-0 bg-gray-100 py-2">{timeGroup}</h3>
                    <ul>
                      {items.map((history, index) => (
                        <li 
                          key={index} 
                          className={`px-4 py-3 border-l-4 ${
                            hoveredItem === `${timeGroup}-${index}` ? 'border-blue-500' : 'border-transparent'
                          } ${getItemBackground(index, hoveredItem === `${timeGroup}-${index}`)} 
                          hover:bg-blue-50 transition-all duration-150 cursor-pointer`}
                          onClick={() => onHistoryItemClick(history.query)}
                          onMouseEnter={() => setHoveredItem(`${timeGroup}-${index}`)}
                          onMouseLeave={() => setHoveredItem(null)}
                        >
                          <div className="flex items-center">
                            <div className="flex-shrink-0 mr-3">
                              <div className={`rounded-full p-2 ${
                                hoveredItem === `${timeGroup}-${index}` ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'
                              } transition-colors`}>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                                </svg>
                              </div>
                            </div>
                            <div className="flex-grow overflow-hidden">
                              <div className="text-sm font-medium text-gray-900 truncate">
                                {history.query}
                              </div>
                              <div className="flex items-center mt-1 text-xs text-gray-500">
                                <span className="truncate">
                                  {formatDistanceToNow(new Date(history.timestamp), { addSuffix: true })}
                                </span>
                                <span className="mx-1.5">•</span>
                                <span className={`font-medium ${
                                  hoveredItem === `${timeGroup}-${index}` ? 'text-blue-600' : 'text-gray-600'
                                }`}>
                                  {history.tools.length} Tool{history.tools.length !== 1 ? 's' : ''}
                                </span>
                              </div>
                            </div>
                            <div className="flex-shrink-0 ml-2">
                              <button 
                                className={`p-1.5 rounded-full ${
                                  hoveredItem === `${timeGroup}-${index}` ? 'bg-blue-100 text-blue-600' : 'bg-transparent text-gray-400'
                                } hover:bg-blue-100 hover:text-blue-600 transition-colors`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onViewToolsClick(history);
                                }}
                                aria-label="View tools"
                                title="View tools"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                                </svg>
                              </button>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Sidebar Footer */}
          <div className="p-3 border-t border-gray-100 bg-gray-50 text-xs text-gray-500 text-center">
            {historyList.length > 0 && 
              `${historyList.length} search${historyList.length !== 1 ? 'es' : ''} in your history`
            }
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default HistorySidebar;